import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@shared/auth/useAuth';
import { loadMockAuth, isMockAuthAvailable } from '@shared/auth/mock-auth-loader';
import type { MockAuthUser } from '@shared/auth/mock-auth';
import { safeReturnTo } from '@shared/auth/safe-return-to';
import { getRuntimeConfig } from '../../config/runtime-config';

const CONSOLE_ROLES = new Set(['nexsoss:support_l2', 'nexsoss:support_l3', 'nexsoss:platform_admin']);

export function LoginScreen() {
  const { login, signInMockRole, authMode, isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'));
  const cfg = getRuntimeConfig();
  const [mockUsers, setMockUsers] = useState<MockAuthUser[]>([]);

  useEffect(() => {
    if (isAuthenticated) window.location.assign(returnTo);
  }, [isAuthenticated, returnTo]);

  useEffect(() => {
    if (authMode !== 'mock') return;
    if (!isMockAuthAvailable()) return;
    let cancelled = false;
    void loadMockAuth().then((m) => {
      if (cancelled) return;
      // Only the roles this console has any use for -- the mock
      // picker for the OPERATOR SPA shows all six; this one is
      // deliberately narrower so a support engineer testing locally
      // isn't offered site_supervisor/foh_operator/service_operator,
      // which RoleGuard would reject anyway.
      setMockUsers((m.l() as MockAuthUser[]).filter((u) => u.roles.some((r) => CONSOLE_ROLES.has(r))));
    });
    return () => {
      cancelled = true;
    };
  }, [authMode]);

  const users = useMemo(() => mockUsers, [mockUsers]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          background: 'var(--nx-card)',
          border: '1px solid var(--nx-border)',
          borderRadius: 'var(--nx-r)',
          padding: 28,
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--nx-ff-h)',
            background: 'var(--nx-grad)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
            fontSize: 24,
            letterSpacing: '0.14em',
            margin: 0,
          }}
        >
          NEXSOSS PLATFORM SUPPORT
        </h1>
        <p style={{ color: 'var(--nx-muted)', marginTop: 8 }}>
          {authMode === 'mock'
            ? 'Local mock authentication — pick a support role to sign in.'
            : `Sign in with ${cfg.zitadel.issuer}`}
        </p>

        <button
          type="button"
          style={{
            marginTop: 16,
            padding: '12px 16px',
            border: 0,
            borderRadius: 'var(--nx-r)',
            background: 'var(--nx-grad)',
            color: 'var(--nx-white)',
            fontFamily: 'var(--nx-ff-h)',
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            width: '100%',
          }}
          onClick={() => void login(returnTo)}
        >
          Sign in
        </button>

        {authMode === 'mock' && (
          <div style={{ marginTop: 18 }}>
            <div
              style={{
                color: 'var(--nx-cyan)',
                fontFamily: 'var(--nx-ff-h)',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              Or pick a mock role
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {users.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => {
                    const role = u.roles[0];
                    if (!role) return;
                    signInMockRole(role);
                    window.location.assign(returnTo);
                  }}
                  style={{
                    textAlign: 'left',
                    padding: '8px 12px',
                    borderRadius: 'var(--nx-r)',
                    border: '1px solid var(--nx-border)',
                    background: 'var(--nx-bg3)',
                    color: 'var(--nx-text)',
                  }}
                >
                  {u.displayName}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
